<template>
  <div class="col-xl-12">
    <div class="card dispatch-dashboard-card" ref="lorryCard">
      <!-- HEADER -->
      <div class="card-header card-no-border">
        <div
          class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2"
        >
          <h5>DISPATCH SUMMARY</h5>

          <div class="d-flex align-items-center gap-2">
            <div
              class="refresh-icon"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </div>

            <div class="icon-btn" @click="toggleFullScreen" title="Fullscreen">
              <i
                :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"
              ></i>
            </div>
          </div>
        </div>

        <div
          class="d-flex justify-content-start align-items-start flex-wrap gap-2"
        >
          <!-- FILTERS -->
          <div class="header-top">
            <div class="d-flex gap-2 flex-wrap">
              <!-- Distributor -->
              <select
                v-model="selectedDistributor"
                class="form-select"
                @focus="lazyLoadDistributors"
                @change="fetchStats"
                :disabled="isDistributor || isDistributorBackOffice"
                style="width: 250px"
              >
                <option v-if="!isDistributor && !isDistributorBackOffice" value="">All Distributors</option>

                <option
                  v-for="d in distributors"
                  :key="d.profile_id"
                  :value="d.profile_id"
                >
                  {{ d.name }}
                </option>
              </select>

              <!-- From Date -->
              <input
                type="date"
                v-model="selectedFromDate"
                class="form-control"
                style="width: 140px"
                @change="fetchStats"
              />

              <!-- To Date -->
              <input
                type="date"
                v-model="selectedToDate"
                class="form-control"
                style="width: 140px"
                @change="fetchStats"
              />

              <router-link
                :to="{
                  name: 'VehicleWiseSalesDetail',
                  query: {
                    distributor: selectedDistributor,
                    from_date: selectedFromDate,
                    to_date: selectedToDate,
                  },
                }"
                target="_blank"
                class="btn btn-sm view-more-btn"
                title="View Details"
              >
                View Details
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- BODY -->
      <div class="card-body pt-0">
        <!-- LOADING -->
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <!-- ERROR -->
        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
        </div>

        <!-- CONTENT -->
        <div v-else class="content-grid">
          <!-- LEFT: Donut Chart -->
          <div class="chart-box dispatch-chart-panel">
            <div class="dispatch-panel-header">
              <div>
                <h6 class="section-title mb-0">Dispatch Status</h6>

                <span class="dispatch-panel-subtitle">
                  Lorry movement overview
                </span>
              </div>

              <div class="dispatch-total-pill">
                {{ dispatchSummary.total }}
                <span>Total</span>
              </div>
            </div>

            <div class="dispatch-chart-wrap">
              <apexchart
                :key="chartKey"
                type="donut"
                :height="chartHeight"
                :options="donutOptions"
                :series="donutSeries"
              />
            </div>

            <div class="dispatch-chart-summary">
              <div class="dispatch-summary-item success">
                <span class="summary-dot"></span>
                <span>Dispatched</span>
                <strong>{{ dispatchSummary.dispatched }}</strong>
              </div>

              <div class="dispatch-summary-item warning">
                <span class="summary-dot"></span>
                <span>Idle</span>
                <strong>{{ dispatchSummary.non_dispatched }}</strong>
              </div>
            </div>
          </div>

          <!-- RIGHT: Stats -->
          <div class="stats-box">
            <!-- Dispatch Counts -->
            <div class="stat-group dispatch-metric-group">
              <h6 class="section-title">Lorry Overview</h6>

              <div class="stat-cards">
                <div class="stat-card total">
                  <span class="stat-label">Total Lorries</span>
                  <span class="stat-value">{{ dispatchSummary.total }}</span>
                </div>

                <div class="stat-card dispatched">
                  <span class="stat-label">Dispatched</span>

                  <span class="stat-value">
                    {{ dispatchSummary.dispatched }}
                  </span>
                </div>

                <div class="stat-card non-dispatched">
                  <span class="stat-label">Idle</span>

                  <span class="stat-value">
                    {{ dispatchSummary.non_dispatched }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Invoice Summary -->
            <div
              class="stat-group mt-3 dispatch-metric-group invoice-metric-group"
            >
              <h6 class="section-title">Invoice Summary</h6>

              <div class="stat-cards">
                <div class="stat-card invoice-val">
                  <span class="stat-label">Total Value</span>

                  <span class="stat-value">
                    {{ formatCurrency(invoiceSummary.total_value) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- BOTTOM: Top 5 Items -->
          <div
            class="stat-group top-products-wide"
            v-if="topVariants && topVariants.length"
          >
            <div class="top-gallery-header">
              <div>
                <h6 class="section-title mb-0">Top 5 Items</h6>
              </div>

              <div class="top-gallery-live">
                <span class="live-dot"></span>

                <span>
                  {{ currentTopVariantIndex + 1 }} of
                  {{ topVariants.length }}
                </span>
              </div>
            </div>

            <div class="top-gallery-card" v-if="activeTopVariant">
              <button
                type="button"
                class="gallery-nav-btn left"
                @click="prevTopVariant"
                :disabled="topVariants.length <= 1"
                title="Previous item"
              >
                <i class="fa fa-chevron-left"></i>
              </button>

              <div class="top-gallery-image-wrap">
                <img
                  v-if="
                    activeTopVariant.item_img &&
                    !activeTopVariant.image_error
                  "
                  :src="getItemImageUrl(activeTopVariant.item_img)"
                  :alt="activeTopVariant.variant_name"
                  class="top-gallery-image"
                  @error="handleItemImageError(activeTopVariant)"
                />

                <div v-else class="top-gallery-image-fallback">
                  <i class="fa fa-box"></i>
                </div>
              </div>

              <div class="top-gallery-info">
                <div class="top-gallery-name">
                  {{ activeTopVariant.variant_name }}
                </div>

                <div class="top-gallery-count">
                  <span class="count-value">
                    {{
                      Number(
                        activeTopVariant.total_quantity || 0,
                      ).toLocaleString()
                    }}
                  </span>

                  <span class="count-label">Qty Sold</span>
                </div>
              </div>

              <button
                type="button"
                class="gallery-nav-btn right"
                @click="nextTopVariant"
                :disabled="topVariants.length <= 1"
                title="Next item"
              >
                <i class="fa fa-chevron-right"></i>
              </button>
            </div>

            <div class="top-gallery-dots">
              <button
                v-for="(variant, index) in topVariants"
                :key="variant.variant_id"
                type="button"
                class="top-gallery-dot"
                :class="{ active: index === currentTopVariantIndex }"
                @click="setTopVariant(index)"
                :title="variant.variant_name"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import {
  fetchDistributors,
  fetchLorryWiseSales,
} from "@/services/dataService";
import { getApiErrorMessage } from "@/utils/apiError";

export default {
  components: {
    apexchart: VueApexCharts,
  },

  data() {
    const userLabel = localStorage.getItem("user_label");
    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      isDistributor: userLabel === "DISTRIBUTOR",
      isDistributorBackOffice: userLabel === "DISTRIBUTOR BACK OFFICE",

      loading: false,
      error: null,
      isFullScreen: false,

      chartHeight: 170,
      chartKey: 0,

      distributors: [],
      selectedDistributor: "",
      selectedFromDate: new Date().toISOString().split("T")[0],
      selectedToDate: new Date().toISOString().split("T")[0],

      dispatchSummary: {
        total: 0,
        dispatched: 0,
        non_dispatched: 0,
      },

      invoiceSummary: {
        total_value: 0,
        total_quantity: 0,
      },

      categoryQuantities: [],

      topVariants: [],
      currentTopVariantIndex: 0,
      topVariantTimer: null,

      distributorsLoaded: false,

      /*
       * Reliability/performance controls only.
       * These do not change the existing UI or business logic.
       */
      distributorRequestPromise: null,
      statsRequestSequence: 0,
      componentActive: true,
    };
  },

  mounted() {
    if (this.isDistributor) {
      this.selectedDistributor = this.profileId;
      this.lazyLoadDistributors();
    } else if (this.isDistributorBackOffice) {
      this.selectedDistributor = this.reportingProfileId;
      this.lazyLoadDistributors();
    }

    this.fetchStats();

    document.addEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );
  },

  beforeUnmount() {
    this.componentActive = false;

    /*
     * Invalidates an unfinished response so it cannot update
     * this component after the user navigates away.
     */
    this.statsRequestSequence += 1;

    this.stopTopVariantGallery();

    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );
  },

  computed: {
    yearOptions() {
      const years = [];

      for (
        let year = new Date().getFullYear();
        year >= 2000;
        year--
      ) {
        years.push(year);
      }

      return years;
    },

    donutSeries() {
      return [
        this.dispatchSummary.dispatched,
        this.dispatchSummary.non_dispatched,
      ];
    },

    activeTopVariant() {
      const variants = this.topVariants || [];

      if (!variants.length) {
        return null;
      }

      return (
        variants[this.currentTopVariantIndex] ||
        variants[0]
      );
    },

    donutOptions() {
      return {
        chart: {
          type: "donut",
          toolbar: {
            show: false,
          },
          sparkline: {
            enabled: false,
          },
          animations: {
            enabled: true,
            speed: 500,
          },
        },

        labels: ["Dispatched", "Idle"],

        colors: ["#10b981", "#f97316"],

        stroke: {
          width: 0,
        },

        legend: {
          show: false,
        },

        dataLabels: {
          enabled: false,
        },

        plotOptions: {
          pie: {
            expandOnClick: false,

            donut: {
              size: "72%",

              labels: {
                show: true,

                name: {
                  show: true,
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#64748b",
                  offsetY: -4,
                },

                value: {
                  show: true,
                  fontSize: "24px",
                  fontWeight: 900,
                  color: "#0f172a",
                  offsetY: 5,

                  formatter: (value) =>
                    Number(value || 0).toLocaleString(),
                },

                total: {
                  show: true,
                  showAlways: true,
                  label: "Total Lorries",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#64748b",

                  formatter: () =>
                    Number(
                      this.dispatchSummary.total || 0,
                    ).toLocaleString(),
                },
              },
            },
          },
        },

        tooltip: {
          y: {
            formatter: (value) =>
              `${Number(value || 0).toLocaleString()} lorries`,
          },
        },
      };
    },
  },

  methods: {
    /*
     * Same fullscreen behavior as the working version.
     * It is now a named method so the listener can be removed.
     */
    handleFullscreenChange() {
      const isFullScreen = !!document.fullscreenElement;

      this.isFullScreen = isFullScreen;

      this.$nextTick(() => {
        this.chartHeight = isFullScreen
          ? Math.max(300, window.innerHeight - 250)
          : 170;

        this.chartKey += 1;
      });
    },

    /*
     * Same lazy distributor loading.
     * Repeated focus events now share one running request.
     */
    async lazyLoadDistributors() {
      if (this.distributorsLoaded) {
        return;
      }

      if (this.distributorRequestPromise) {
        await this.distributorRequestPromise;
        return;
      }

      this.distributorRequestPromise =
        this.fetchDistributorList();

      try {
        const loaded =
          await this.distributorRequestPromise;

        if (loaded) {
          this.distributorsLoaded = true;
        }
      } finally {
        this.distributorRequestPromise = null;
      }
    },

    /*
     * Same refresh behavior.
     * The existing loading class already prevents pointer interaction,
     * and this guard prevents accidental direct duplicate calls.
     */
    handleRefresh() {
      if (this.loading) {
        return;
      }

      this.fetchStats();
    },

    async fetchDistributorList() {
      try {
        const response = await fetchDistributors(
          this.profileId,
          this.token,
        );

        if (!this.componentActive) {
          return false;
        }

        this.distributors = Array.isArray(
          response.data?.data,
        )
          ? response.data.data
          : [];

        return true;
      } catch (error) {
        console.error(
          "Distributor list API failed:",
          error?.response?.status,
          error?.response?.data || error,
        );

        /*
         * Keep the existing distributor array unchanged
         * so a temporary failure does not remove loaded options.
         */
        return false;
      }
    },

    async fetchStats() {
      /*
       * Every filter change is still allowed to start a request.
       * Only the latest request is allowed to update the card.
       */
      const requestId =
        ++this.statsRequestSequence;

      try {
        /*
         * Loading and error functionality remains exactly
         * the same as the previous working version.
         */
        this.loading = true;
        this.error = null;

        /*
         * Payload remains exactly unchanged.
         */
        const payload = {
          profile_id:
            this.selectedDistributor ||
            this.profileId,

          from_date: this.selectedFromDate,
          to_date: this.selectedToDate,
        };

        const response =
          await fetchLorryWiseSales(
            payload,
            this.token,
          );

        /*
         * A newer filter request has started.
         * Ignore this older response.
         */
        if (
          !this.componentActive ||
          requestId !==
            this.statsRequestSequence
        ) {
          return;
        }

        if (response.data.success) {
          const data = response.data.data;

          this.dispatchSummary =
            data.dispatch_summary;

          this.invoiceSummary =
            data.invoice_summary;

          this.topVariants = (
            data.top_variants || []
          ).map((item) => ({
            ...item,
            image_error: false,
          }));

          this.currentTopVariantIndex = 0;

          this.startTopVariantGallery();
        } else {
          /*
           * Existing working error behavior preserved.
           */
          this.error = "No data available";
        }
      } catch (error) {
        /*
         * Do not show an error from an outdated request.
         */
        if (
          !this.componentActive ||
          requestId !==
            this.statsRequestSequence
        ) {
          return;
        }

        console.error(
          "Dashboard API failed:",
          error?.response?.status,
          error?.response?.data || error,
        );

        this.error = getApiErrorMessage(error);
      } finally {
        /*
         * An older request must not stop the spinner
         * while a newer request is still running.
         */
        if (
          this.componentActive &&
          requestId ===
            this.statsRequestSequence
        ) {
          this.loading = false;
        }
      }
    },

    getCatPercent(qty) {
      return Math.round(
        (qty / this.maxCategoryQty) * 100,
      );
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "LKR",
        maximumFractionDigits: 0,
      }).format(value);
    },

    stopTopVariantGallery() {
      if (this.topVariantTimer) {
        clearInterval(this.topVariantTimer);
        this.topVariantTimer = null;
      }
    },

    startTopVariantGallery() {
      this.stopTopVariantGallery();

      const variants = this.topVariants || [];

      if (variants.length <= 1) {
        return;
      }

      this.topVariantTimer = setInterval(() => {
        this.nextTopVariant();
      }, 3500);
    },

    nextTopVariant() {
      const variants = this.topVariants || [];

      if (!variants.length) {
        return;
      }

      this.currentTopVariantIndex =
        (this.currentTopVariantIndex + 1) %
        variants.length;
    },

    prevTopVariant() {
      const variants = this.topVariants || [];

      if (!variants.length) {
        return;
      }

      this.currentTopVariantIndex =
        (this.currentTopVariantIndex -
          1 +
          variants.length) %
        variants.length;
    },

    setTopVariant(index) {
      this.currentTopVariantIndex = index;
      this.startTopVariantGallery();
    },

    getItemImageUrl(image) {
      if (!image) {
        return "";
      }

      const imageValue = String(image).trim();

      if (
        imageValue.startsWith("http://") ||
        imageValue.startsWith("https://") ||
        imageValue.startsWith("data:image")
      ) {
        return imageValue;
      }

      const cleanBase64 =
        imageValue.replace(/\s/g, "");

      if (
        cleanBase64.startsWith("iVBOR") ||
        cleanBase64.startsWith("/9j/") ||
        cleanBase64.startsWith("R0lG") ||
        cleanBase64.startsWith("UklGR")
      ) {
        if (
          cleanBase64.startsWith("/9j/")
        ) {
          return `data:image/jpeg;base64,${cleanBase64}`;
        }

        if (
          cleanBase64.startsWith("R0lG")
        ) {
          return `data:image/gif;base64,${cleanBase64}`;
        }

        if (
          cleanBase64.startsWith("UklGR")
        ) {
          return `data:image/webp;base64,${cleanBase64}`;
        }

        return `data:image/png;base64,${cleanBase64}`;
      }

      const baseUrl =
        process.env.VUE_APP_IMAGE_BASE_URL ||
        process.env.VUE_APP_API_BASE_URL ||
        "";

      if (!baseUrl) {
        return imageValue;
      }

      return `${baseUrl.replace(
        /\/$/,
        "",
      )}/${imageValue.replace(/^\//, "")}`;
    },

    handleItemImageError(variant) {
      variant.image_error = true;
    },

    /*
     * Same fullscreen functionality as before.
     */
    toggleFullScreen() {
      const element = this.$refs.lorryCard;

      if (!document.fullscreenElement) {
        element.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    },
  },
};
</script>

<style scoped>
/* ─────────────────────────────────────────────
   Base layout
───────────────────────────────────────────── */
.content-grid {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}

.section-title {
  color: #1f2937;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  margin-bottom: 7px;
}

/* ─────────────────────────────────────────────
   Main Dispatch Summary Card
───────────────────────────────────────────── */
.dispatch-dashboard-card {
  height: 100%;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.dispatch-dashboard-card .card-header {
  padding: 10px 14px 8px !important;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.dispatch-dashboard-card .card-header h5 {
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: -0.01em;
}

.dispatch-dashboard-card .card-body {
  padding: 8px 14px 12px !important;
}

/* ─────────────────────────────────────────────
   Compact Chart Panel
───────────────────────────────────────────── */
.dispatch-chart-panel {
  min-height: 205px;
  max-height: 215px;
  padding: 9px;
  border: 1px solid #e8edf4;
  border-radius: 13px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.035);
  display: flex;
  flex-direction: column;
}

.dispatch-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}

.dispatch-panel-subtitle {
  display: none;
}

.dispatch-total-pill {
  display: none;
}

.dispatch-chart-wrap {
  height: 162px;
  min-height: 162px;
  max-height: 162px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.dispatch-chart-wrap :deep(.vue-apexcharts),
.dispatch-chart-panel :deep(.apexcharts-canvas) {
  max-width: 180px !important;
  margin: 0 auto !important;
}

.dispatch-chart-summary {
  display: none;
}

/* ─────────────────────────────────────────────
   Right Side Metric Groups
───────────────────────────────────────────── */
.stats-box {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dispatch-metric-group {
  padding: 8px;
  border: 1px solid #e8edf4;
  border-radius: 13px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
}

.dispatch-metric-group.mt-3 {
  margin-top: 0 !important;
}

.dispatch-metric-group .section-title {
  margin-bottom: 7px;
}

/* ─────────────────────────────────────────────
   Stat Cards
───────────────────────────────────────────── */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.stat-card {
  min-width: 0;
  min-height: 52px;
  padding: 7px 8px;
  border-radius: 11px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  border: 1px solid rgba(15, 23, 42, 0.04);
  box-shadow: none;
}

.stat-label {
  color: #64748b;
  font-size: 9.5px;
  font-weight: 850;
  line-height: 1.15;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  word-break: normal;
  hyphens: none;
}

.stat-value {
  margin-top: 4px;
  font-size: 20px;
  font-weight: 950;
  line-height: 1;
  letter-spacing: -0.03em;
}

.stat-card.total {
  background: #f1edff;
}

.stat-card.total .stat-value {
  color: #6c5ce7;
}

.stat-card.dispatched {
  background: #e9f9f1;
}

.stat-card.dispatched .stat-value {
  color: #00b894;
}

.stat-card.non-dispatched {
  background: #fff1e6;
}

.stat-card.non-dispatched .stat-value {
  color: #fb7185;
}

.stat-card.invoice-qty {
  background: #fff8e7;
}

.stat-card.invoice-qty .stat-value {
  color: #fdcb6e;
}

/* Invoice summary compact horizontal card */
.invoice-metric-group .stat-cards {
  grid-template-columns: 1fr;
}

.invoice-metric-group .stat-card.invoice-val {
  min-height: 48px;
  padding: 8px 10px;
  background: #eaf3ff;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.invoice-metric-group .stat-card.invoice-val .stat-label {
  margin: 0;
  white-space: nowrap;
}

.invoice-metric-group .stat-card.invoice-val .stat-value {
  margin: 0;
  color: #0984e3;
  font-size: 16px;
  font-weight: 950;
  line-height: 1;
  white-space: nowrap;
}

/* ─────────────────────────────────────────────
   Old Category Cards - kept safely
───────────────────────────────────────────── */
.category-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.category-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f4f6fa;
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 90px;
  text-align: center;
}

.cat-card-qty {
  font-size: 16px;
  font-weight: 700;
  color: #3b82f6;
  line-height: 1.2;
}

.cat-card-name {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}

/* ─────────────────────────────────────────────
   Loading / Error
───────────────────────────────────────────── */
.spinner-container {
  display: flex;
  justify-content: center;
  height: 150px;
  align-items: center;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #6c5ce7;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-container {
  text-align: center;
}

.error-message {
  color: red;
  font-weight: bold;
}

/* ─────────────────────────────────────────────
   Header Controls / Filters - unchanged look
───────────────────────────────────────────── */
.header-top {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

select {
  width: auto;
  font-size: 15px;
}

select option {
  font-size: 15px;
}

.refresh-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
}

.refresh-icon:hover {
  background: #f1f3f5;
  color: #000;
}

.refresh-icon.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
}

.icon-btn:hover {
  background: #f1f3f5;
  color: #000;
}

.view-more-btn {
  background-color: #000;
  border: 1px solid #000;
  color: #fff;
}

/* ─────────────────────────────────────────────
   Top 5 Gallery - kept as current
───────────────────────────────────────────── */
.top-gallery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}

.top-gallery-subtitle {
  display: block;
  margin-top: 1px;
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
}

.top-gallery-live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e6eaf0;
  color: #64748b;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.13);
  animation: livePulse 1.4s ease-in-out infinite;
}

@keyframes livePulse {
  0% {
    transform: scale(0.9);
    opacity: 0.75;
  }

  50% {
    transform: scale(1.15);
    opacity: 1;
  }

  100% {
    transform: scale(0.9);
    opacity: 0.75;
  }
}

.top-gallery-card {
  position: relative;
  min-height: 108px;
  overflow: hidden;
  border: 1px solid #e7ebf3;
  border-radius: 13px;
  background: radial-gradient(
      circle at top left,
      rgba(112, 100, 245, 0.1),
      transparent 35%
    ),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 5px 14px rgba(15, 23, 42, 0.05);
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  padding: 8px 34px 8px 34px;
}

.top-gallery-rank {
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 3;
  height: 20px;
  min-width: 31px;
  padding: 3px 7px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.88);
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
}

.top-gallery-image-wrap {
  width: 82px;
  height: 82px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.top-gallery-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
  display: block;
}

.top-gallery-image-fallback {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.top-gallery-info {
  min-width: 0;
}

.top-gallery-name {
  color: #111827;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.top-gallery-count {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 90px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #dfe5ff;
}

.count-value {
  color: #4338ca;
  font-size: 17px;
  font-weight: 900;
  line-height: 1;
}

.count-label {
  color: #64748b;
  font-size: 10px;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.gallery-nav-btn {
  position: absolute;
  top: 50%;
  z-index: 4;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.gallery-nav-btn.left {
  left: 6px;
}

.gallery-nav-btn.right {
  right: 6px;
}

.gallery-nav-btn:hover:not(:disabled) {
  background: #111827;
  color: #ffffff;
  border-color: #111827;
}

.gallery-nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.top-gallery-dots {
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-top: 7px;
}

.top-gallery-dot {
  width: 6px;
  height: 6px;
  border: none;
  border-radius: 999px;
  background: #cbd5e1;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.top-gallery-dot.active {
  width: 18px;
  background: #7064f5;
}

/* ─────────────────────────────────────────────
   Responsive
───────────────────────────────────────────── */
@media (max-width: 1399px) {
  .content-grid {
    grid-template-columns: 190px minmax(0, 1fr);
    gap: 9px;
  }

  .dispatch-chart-panel {
    min-height: 195px;
    max-height: 205px;
  }

  .dispatch-chart-wrap {
    height: 150px;
    min-height: 150px;
    max-height: 150px;
  }

  .dispatch-chart-wrap :deep(.vue-apexcharts),
  .dispatch-chart-panel :deep(.apexcharts-canvas) {
    max-width: 165px !important;
  }

  .stat-card {
    min-height: 48px;
    padding: 6px 7px;
  }

  .stat-value {
    font-size: 18px;
  }

  .invoice-metric-group .stat-card.invoice-val .stat-value {
    font-size: 15px;
  }

  .top-gallery-card {
    grid-template-columns: 72px minmax(0, 1fr);
    min-height: 96px;
    gap: 8px;
    padding: 7px 31px 7px 31px;
  }

  .top-gallery-image-wrap {
    width: 72px;
    height: 72px;
  }

  .top-gallery-name {
    font-size: 12px;
  }

  .count-value {
    font-size: 15px;
  }

  .top-gallery-count {
    margin-top: 6px;
    padding: 5px 9px;
  }
}

@media (max-width: 991px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .dispatch-chart-panel {
    min-height: 205px;
    max-height: none;
  }

  .dispatch-chart-wrap {
    height: 155px;
    min-height: 155px;
    max-height: 155px;
  }
}

@media (max-width: 768px) {
  .top-gallery-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .top-gallery-card {
    grid-template-columns: 72px minmax(0, 1fr);
    min-height: 98px;
    text-align: left;
  }

  .top-gallery-image-wrap {
    width: 72px;
    height: 72px;
  }
}

@media (max-width: 576px) {
  .stat-cards {
    grid-template-columns: 1fr;
  }

  .invoice-metric-group .stat-card.invoice-val {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* Better organized 2-row dispatch layout */
.content-grid {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  grid-template-areas:
    "chart stats"
    "top5 top5";
  gap: 10px;
  align-items: start;
}

.dispatch-chart-panel {
  grid-area: chart;
}

.stats-box {
  grid-area: stats;
}

.top-products-wide {
  grid-area: top5;
  margin-top: 0 !important;
  padding: 9px;
  border: 1px solid #e8edf4;
  border-radius: 13px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
}

.top-products-wide .top-gallery-header {
  margin-bottom: 7px;
}

/* Full-width but compact Top 5 card */
.top-products-wide .top-gallery-card {
  width: 100%;
  max-width: none;
  min-height: 82px;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 11px;
  padding: 7px 42px 7px 42px;
  border-radius: 12px;
}

.top-products-wide .top-gallery-image-wrap {
  width: 72px;
  height: 72px;
  border-radius: 12px;
}

.top-products-wide .top-gallery-image {
  padding: 5px;
}

.top-products-wide .top-gallery-info {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.top-products-wide .top-gallery-name {
  min-width: 0;
  font-size: 13px;
  font-weight: 850;
  -webkit-line-clamp: 1;
}

.top-products-wide .top-gallery-count {
  flex-shrink: 0;
  margin-top: 0;
  padding: 6px 12px;
}

.top-products-wide .count-value {
  font-size: 16px;
}

.top-products-wide .count-label {
  font-size: 10px;
}

.top-products-wide .top-gallery-dots {
  margin-top: 6px;
}

/* Medium screens */
@media (max-width: 1399px) {
  .content-grid {
    grid-template-columns: 200px minmax(0, 1fr);
    gap: 9px;
  }

  .top-products-wide .top-gallery-card {
    min-height: 78px;
    grid-template-columns: 70px minmax(0, 1fr);
    padding: 7px 38px;
  }

  .top-products-wide .top-gallery-image-wrap {
    width: 66px;
    height: 66px;
  }

  .top-products-wide .top-gallery-name {
    font-size: 12.5px;
  }

  .top-products-wide .count-value {
    font-size: 15px;
  }
}

/* Tablet / mobile */
@media (max-width: 991px) {
  .content-grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      "chart"
      "stats"
      "top5";
  }

  .top-products-wide .top-gallery-card {
    max-width: 100%;
  }
}

@media (max-width: 576px) {
  .top-products-wide .top-gallery-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .top-products-wide .top-gallery-count {
    margin-top: 0;
  }
}

/* Request-state additions only */
.refresh-icon,
.icon-btn {
  border: none;
  background: transparent;
  padding: 0;
}

.refresh-icon:disabled,
.icon-btn:disabled {
  cursor: not-allowed;
}

.dispatch-retry-btn {
  min-height: 30px;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.dispatch-retry-btn:hover:not(:disabled) {
  border-color: #7064f5;
  color: #7064f5;
  background: #f8f7ff;
}

.dispatch-retry-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.content-grid {
  position: relative;
}

.dispatch-refresh-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 20;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  color: #64748b;
  font-size: 10.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.dispatch-inline-warning {
  grid-column: 1 / -1;
  padding: 7px 9px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 10.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dispatch-inline-warning button {
  flex-shrink: 0;
  padding: 3px 7px;
  border: 1px solid #fdba74;
  border-radius: 6px;
  background: #ffffff;
  color: #9a3412;
  font-size: 10.5px;
  font-weight: 700;
  cursor: pointer;
}

.dispatch-inline-warning button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.dispatch-content-refreshing
  :deep(.apexcharts-canvas) {
  opacity: 0.78;
  transition: opacity 0.2s ease;
}
</style>
